import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchAllDoctors } from "../../services/api";
import "./doctors.css";

export default function DoctorsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [specialty, setSpecialty] = useState("all");
  const [city, setCity] = useState("all");

  useEffect(() => {
    fetchAllDoctors()
      .then(data => {
        setDoctors(data);
        setLoading(false);
      })
      .catch(err => {
        setError("Failed to load doctors");
        setLoading(false);
      });
  }, []);

  const specialties = useMemo(
    () => ["all", ...new Set(doctors.map((d) => d.specialty).filter(Boolean))],
    [doctors]
  );
  const cities = useMemo(
    () => ["all", ...new Set(doctors.map((d) => d.city).filter(Boolean))],
    [doctors]
  );

  const filtered = useMemo(() => {
    return doctors.filter((d) => {
      const name = `${d.first_name || ''} ${d.last_name || ''}`.toLowerCase();
      const textOk =
        !q ||
        name.includes(q.toLowerCase()) ||
        (d.specialty && d.specialty.toLowerCase().includes(q.toLowerCase())) ||
        (d.city && d.city.toLowerCase().includes(q.toLowerCase()));
      const specOk = specialty === "all" || d.specialty === specialty;
      const cityOk = city === "all" || d.city === city;
      return textOk && specOk && cityOk;
    });
  }, [doctors, q, specialty, city]);

  if (loading) return <div className="doctors-page">Loading doctors...</div>;
  if (error) return <div className="doctors-page" style={{ color: 'red' }}>{error}</div>;

  return (
    <section className="doctors-page">
      <header className="doctors-header">
        <h1>{t("doctors.title")}</h1>
        <p className="subtitle">{t("doctors.subtitle")}</p>
      </header>

      <div className="filters">
        <input
          type="text"
          className="search-input"
          placeholder={t("doctors.search_placeholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Search doctors"
        />
        <div className="selects">
          <label className="select">
            <span>{t("doctors.specialty")}</span>
            <select value={specialty} onChange={(e) => setSpecialty(e.target.value)}>
              {specialties.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label className="select">
            <span>{t("doctors.city")}</span>
            <select value={city} onChange={(e) => setCity(e.target.value)}>
              {cities.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <ul className="doc-page-list">
        {filtered.map((d) => (
          <li key={d.id} className="doc-page-card">
            <div className="doc-main">
              <div className="avatar" aria-hidden>
                <span className="initials">
                  {(d.first_name?.[0] || '') + (d.last_name?.[0] || '')}
                </span>
              </div>
              <div className="meta">
                <h3 className="name">{d.first_name} {d.last_name}</h3>
                <div className="row">
                  <span className="badge">{d.specialty}</span>
                  <span className="dot" />
                  <span className="muted">{d.city || 'Tunis'}</span>
                </div>

                <div className="row">
                  <div className="stars" title={`${d.rating || 5} / 5`}>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <span
                        key={i}
                        className={`star ${i + 1 <= Math.round(d.rating || 5) ? "filled" : ""}`}
                        aria-hidden
                      >
                        ★
                      </span>
                    ))}
                    <span className="rating">{(d.rating || 5).toFixed(1)}</span>
                  </div>
                  <span className="dot" />
                  <span className="muted">{(d.experienceYears || 5)}+ yrs</span>
                </div>
              </div>
            </div>

            <div className="footer">
              <span className={`status ${d.available ? "online" : "offline"}`}>
                <span className="pulse" aria-hidden />
                {d.available ? t("doctors.online") : t("doctors.offline")}
              </span>

              {/* Prix dynamique */}
              <span className="price">{d.consultation_fee ? `${d.consultation_fee} TND` : "TBD"}</span>

              <button className="cta" onClick={() => navigate(`/doctors/${d.id}`)}>
                {t("doctors.view_profile")}
              </button>
            </div>
          </li>
        ))}
      </ul>

      {filtered.length === 0 && <div className="empty">{t("doctors.empty")}</div>}
    </section>
  );
}
